import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type { Photo, Document as DocType } from '../types';

// Opportunity Photos
export const useOpportunityPhotos = (opportunityId: string | undefined) => {
  return useQuery({
    queryKey: ['opportunityPhotos', opportunityId],
    queryFn: async (): Promise<Photo[]> => {
      if (!opportunityId) throw new Error('Opportunity ID is required');
      try {
        const response = await apiClient.getOpportunityPhotos(parseInt(opportunityId));
        // Handle both direct array and paginated responses
        const responseData = response.data as Photo[] | { count: number; results: Photo[] } | null;
        const photosData = Array.isArray(responseData) 
          ? responseData 
          : (responseData && typeof responseData === 'object' && 'results' in responseData && Array.isArray(responseData.results)
              ? responseData.results 
              : []);
        return photosData;
      } catch (error) {
        console.error('Error fetching opportunity photos:', error);
        return [];
      }
    },
    enabled: !!opportunityId,
  });
};

export const useUploadOpportunityPhotos = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ opportunityId, files, description }: { 
      opportunityId: string; 
      files: File[]; 
      description?: string; 
    }) => {
      const response = await apiClient.uploadOpportunityPhotos(parseInt(opportunityId), files, description);
      return response.data;
    },
    onSuccess: (_, { opportunityId }) => {
      // Invalidate the opportunity photos query to refresh the photos list
      queryClient.invalidateQueries({ queryKey: ['opportunityPhotos', opportunityId] });
    },
  });
};

// Opportunity Documents
export const useOpportunityDocs = (opportunityId: string | undefined) => {
  return useQuery({
    queryKey: ['opportunityDocs', opportunityId],
    queryFn: async (): Promise<DocType[]> => {
      if (!opportunityId) throw new Error('Opportunity ID is required');
      try {
        const response = await apiClient.getOpportunityDocs(parseInt(opportunityId));
        // Handle both direct array and paginated responses
        const responseData = response.data as DocType[] | { count: number; results: DocType[] } | null;
        const docsData = Array.isArray(responseData) 
          ? responseData 
          : (responseData && typeof responseData === 'object' && 'results' in responseData && Array.isArray(responseData.results)
              ? responseData.results 
              : []);
        
        // Ensure each document has a tags array
        return docsData.map((doc: any) => ({
          ...doc,
          tags: Array.isArray(doc.tags) ? doc.tags : []
        }));
      } catch (error) {
        console.error('Error fetching opportunity docs:', error);
        return [];
      }
    },
    enabled: !!opportunityId,
  });
};

export const useUploadOpportunityDocs = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ opportunityId, files }: { 
      opportunityId: string; 
      files: File[]; 
    }) => {
      // Upload each file individually since API accepts single file
      const uploadPromises = files.map(file => 
        apiClient.uploadOpportunityDocs(parseInt(opportunityId), file)
      );
      
      const responses = await Promise.all(uploadPromises);
      return responses.map(response => response.data);
    },
    onSuccess: (_, { opportunityId }) => {
      // Invalidate the opportunity docs query to refresh the documents list
      queryClient.invalidateQueries({ queryKey: ['opportunityDocs', opportunityId] });
    },
  });
};

export const useUpdateDoc = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ docId, tagNames }: { 
      docId: number; 
      tagNames: string; 
    }) => {
      const response = await apiClient.updateDoc(docId, { tag_names: tagNames });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all opportunity docs queries to refresh the documents list
      queryClient.invalidateQueries({ queryKey: ['opportunityDocs'] });
    },
  });
};

export const useDeleteDoc = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ docId, opportunityId }: { docId: number; opportunityId?: number }) => {
      const response = await apiClient.deleteDoc(docId);
      return { data: response.data, opportunityId };
    },
    onSuccess: (result) => {
      // Invalidate all opportunity docs queries to refresh the documents list
      queryClient.invalidateQueries({ queryKey: ['opportunityDocs'] });
      // Also invalidate measurement docs if opportunityId provided
      if (result.opportunityId) {
        queryClient.invalidateQueries({
          queryKey: ['opportunityMeasurementDocs', result.opportunityId.toString()]
        });
      }
    },
  });
};